import React, { useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, isToday } from 'date-fns';
import { es } from 'date-fns/locale';

interface MiniCalendarProps {
  className?: string;
  onDateSelect?: (date: Date) => void;
}

const MiniCalendar: React.FC<MiniCalendarProps> = ({ className = '', onDateSelect }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const navigate = useNavigate();
  const location = useLocation();

  const handlePreviousMonth = useCallback(() => {
    setCurrentMonth(prev => subMonths(prev, 1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setCurrentMonth(prev => addMonths(prev, 1));
  }, []);

  const handleDateClick = useCallback((date: Date) => {
    if (onDateSelect) {
      onDateSelect(date);
    }
    
    // Navigate to calendar page with selected date in week view
    const dateParam = format(date, 'yyyy-MM-dd');
    if (location.pathname !== '/calendar') {
      navigate(`/calendar?date=${dateParam}&view=timeGridWeek`);
    } else {
      // If already on calendar page, just trigger date selection with week view
      window.dispatchEvent(new CustomEvent('miniCalendarDateSelect', { 
        detail: { date, view: 'timeGridWeek' } 
      }));
    }
  }, [navigate, location.pathname, onDateSelect]);

  const handleTodayClick = useCallback(() => {
    const today = new Date();
    setCurrentMonth(today);
    handleDateClick(today);
  }, [handleDateClick]);

  // Generate calendar days
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 }); // Monday
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const weekDays = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-2 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={handlePreviousMonth}
          className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-gray-700"
        >
          <ChevronLeft className="h-3 w-3" />
        </button>
        
        <div className="text-center">
          <h3 className="text-xs font-semibold text-gray-900 capitalize">
            {format(currentMonth, 'MMMM yyyy', { locale: es })}
          </h3>
        </div>
        
        <button
          onClick={handleNextMonth}
          className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-gray-700"
        >
          <ChevronRight className="h-3 w-3" />
        </button>
      </div>

      {/* Week days header */}
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {weekDays.map((day) => (
          <div key={day} className="text-center">
            <span className="text-xs font-medium text-gray-500">{day}</span>
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-0.5">
        {days.map((day, dayIdx) => {
          const isCurrentMonth = isSameMonth(day, currentMonth);
          const isSelected = false; // Could be enhanced to show selected date
          const isDayToday = isToday(day);

          return (
            <button
              key={day.toString()}
              onClick={() => handleDateClick(day)}
              className={`
                h-5 w-5 text-xs rounded-sm font-medium transition-colors duration-150
                ${isCurrentMonth 
                  ? isDayToday
                    ? 'bg-dental-600 text-white hover:bg-dental-700'
                    : 'text-gray-900 hover:bg-dental-100 hover:text-dental-700'
                  : 'text-gray-400 hover:text-gray-600'
                }
                ${isSelected ? 'bg-dental-200 text-dental-800' : ''}
                focus:outline-none focus:ring-1 focus:ring-dental-500
              `}
            >
              {format(day, 'd')}
            </button>
          );
        })}
      </div>

      {/* Today button */}
      <div className="mt-2 pt-1 border-t border-gray-200">
        <button
          onClick={handleTodayClick}
          className="w-full text-xs text-dental-600 hover:text-dental-700 font-medium hover:bg-dental-50 py-1 px-1 rounded transition-colors duration-150"
        >
          Ir a hoy
        </button>
      </div>
    </div>
  );
};

export default MiniCalendar;